import React, { Component, createRef } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import { SafetyNetThemeListWidget } from '.';
import { SafetyNetListWidget } from './SafetyNetListWidget';
import { SafetyNetService } from '../../Services/SafetyNetService';
import { ISafetyNetSearchParams } from '../../Models';
import { IThemeSafetyNet, ISafetyNet } from '../../Models';

interface ISafetyNetSearchWidgetProps {
    configId: number;
    title: string;
    description: string;
    language: string;
    showSearchForm: boolean;
    showAllFaqIfNoThemeSelected: boolean;
    showNoResultIfNoFaq: string | undefined;
    currentFaqId: number | undefined;
    dictMessage: string | undefined;
    dictOpinionLabel: string;
    dictOpinionVotedLabel: string;
    dictSearchButtonLabel: string;
    dictClearButtonLabel: string;
    dictThemeAllLabel: string | undefined;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictSituationTitle: string;
    dictYes: string;
    dictNo: string;
    dictCommentSuccess: string;
    dictCommentFail: string;
    dictSend: string;
    dictCommentInvite: string;
    dictYourComment: string;
    contactUrl: string;
    contactString: string;
}

interface IFaqSearchWidgetState {
    answers: ISafetyNet[];
    currentAnswer: ISafetyNet[];
    themes: IThemeSafetyNet[];
    currentThemeId: number;
    isLoading: boolean;
    allThemes: IThemeSafetyNet[];
}

export class SafetyNetWidget extends Component<
    ISafetyNetSearchWidgetProps,
    IFaqSearchWidgetState
> {
    private readonly faqListRef = createRef<HTMLDivElement>();
    private userClickedTheme = false;

    constructor(props: ISafetyNetSearchWidgetProps) {
        super(props);
        window.history.pushState({}, '', window.location.pathname);
        this.state = {
            answers: [],
            currentAnswer: [],
            themes: [],
            currentThemeId: 0,
            isLoading: true,
            allThemes: [],
        };

        this.handleTheme = this.handleTheme.bind(this);
        this.searchFaq = this.searchFaq.bind(this);
    }

    componentDidMount() {
        void this.getAllFaq(null);
    }

    async getAllFaq(themeId: number | null) {
        try {
            const faqSearchParams: ISafetyNetSearchParams = {
                configId: Number(this.props.configId),
                themeId,
            };
            const allThemes = await SafetyNetService.getAllThemes(
                this.props.configId
            );
            const answers =
                await SafetyNetService.getAllSafetyNet(faqSearchParams);
            const themesWithAnswers = this.getThemesWithAnswers(
                answers,
                allThemes
            );
            const firstThemeId =
                themesWithAnswers.length > 0 ? themesWithAnswers[0].id : 0;
            const currentAnswer = answers.filter((answer) =>
                (answer.themes ?? []).some((t) => t.id === firstThemeId)
            );

            this.setState({
                answers,
                currentAnswer,
                themes: themesWithAnswers,
                isLoading: false,
                currentThemeId: firstThemeId,
                allThemes,
            });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    getThemesWithAnswers(
        faqs: ISafetyNet[],
        allThemes: IThemeSafetyNet[]
    ): IThemeSafetyNet[] {
        const themeIds = new Set(
            faqs.flatMap((faq) => (faq.themes ?? []).map((t) => t.id))
        );
        return Array.from(themeIds)
            .map((id) => allThemes.find((t) => t.id === id))
            .filter((t): t is IThemeSafetyNet => t !== undefined) // Type guard removes undefined
            .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    handleTheme(themeId: number) {
        const currentAnswer = this.state.answers.filter((answer) =>
            (answer.themes ?? []).some((t) => t.id === themeId)
        );

        this.setState({
            currentThemeId: themeId,
            currentAnswer,
        });

        if (this.userClickedTheme && this.faqListRef.current) {
            setTimeout(() => {
                if (this.faqListRef.current) {
                    this.faqListRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
                this.userClickedTheme = false;
            }, 100);
        }
    }

    async searchFaq(themeId: number) {
        await this.getAllFaq(themeId);
        const themes = this.getThemesWithAnswers(
            this.state.answers,
            this.state.allThemes
        );
        let currentThemeId = this.state.themes[0].id;
        if (themes.length > 0) {
            currentThemeId = themes.length >= 1 ? themeId : themes[0].id;
        }

        this.setState({
            currentThemeId,
            isLoading: false,
        });
    }

    getSubtitleByThemeId(themes: IThemeSafetyNet[], id: number): string {
        const theme = themes.find((t) => t.id === id);
        return theme && theme.subTitle ? theme.subTitle : '';
    }

    render() {
        const listFaq = (
            <div className="contact-list-container" ref={this.faqListRef}>
                <SafetyNetListWidget
                    currentAnswerId={this.props.currentFaqId}
                    faqs={this.state.currentAnswer}
                    themeSubTitle={this.getSubtitleByThemeId(
                        this.state.themes,
                        this.state.currentThemeId
                    )}
                    opinionLabel={this.props.dictOpinionLabel}
                    opinionVotedLabel={this.props.dictOpinionVotedLabel}
                    dictNoResultTitle={this.props.dictNoResultTitle}
                    dictNoResultDescription={this.props.dictNoResultDescription}
                    isLoading={this.state.isLoading}
                    showNoResult={
                        this.props.showNoResultIfNoFaq?.toUpperCase() === 'TRUE'
                    }
                    dictYes={this.props.dictYes}
                    dictNo={this.props.dictNo}
                    dictSend={this.props.dictSend}
                    dictCommentSuccess={this.props.dictCommentSuccess}
                    dictCommentFail={this.props.dictCommentFail}
                    dictCommentInvite={this.props.dictCommentInvite}
                    dictYourComment={this.props.dictYourComment}
                    contactString={this.props.contactString}
                    contactUrl={this.props.contactUrl}
                />
            </div>
        );
        return (
            <>
                {this.state.themes.length > 0 && (
                    <div className="before-wrapper">
                        <div className="contact-theme-container">
                            <h3>{this.props.dictSituationTitle}</h3>
                            <div className="container">
                                <div className="row">
                                    <div className="col-12 col-lg-8 offset-lg-2">
                                        {this.state.themes &&
                                            this.state.themes.length > 0 && (
                                                <SafetyNetThemeListWidget
                                                    themes={this.state.themes}
                                                    currentThemeId={
                                                        this.state
                                                            .currentThemeId
                                                    }
                                                    onClick={(themeId) => {
                                                        this.userClickedTheme =
                                                            true;
                                                        this.handleTheme(
                                                            themeId
                                                        );
                                                    }}
                                                    coloredFaq={false}
                                                />
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {this.props.dictMessage && this.props.dictMessage.length > 0 ? (
                    <div className="contact-list-container">
                        <h3>{this.props.dictMessage}</h3>
                        {listFaq}
                    </div>
                ) : (
                    <>{listFaq}</>
                )}
            </>
        );
    }
}

WidgetHelper.register(SafetyNetWidget, 'safety-net-widget');
